import type { Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { unref } from "vue";
import { ApiError, apiCall } from "@/core/utils/api";
import { NotificationType, useNotifyStore } from "./notify-store";
import { useMeetingId, useStagiaireId } from "./routes";
import { useIsActive } from "./meeting";
import { usePermission } from "./permission";

import { meetingsParticipantsResetSignatureDestroy } from "@sgdf/client";
import type {
  MeetingParticipantPatch,
  OpinionParticipantIn,
  OwnParticipationPatch,
} from "@sgdf/client-v2";
import {
  listParticipants,
  patchMyParticipation,
  patchParticipant,
  listParticipantOpinions,
  retrieveParticipant,
  retrieveParticipantOpinion,
  patchParticipantOpinion,
} from "@sgdf/client-v2";
import type { ParticipantUpdatePayload } from "@sgdf/ui-library";
import { internFilter, supervisorFilter } from "~/core/utils/participant-filter";

const PARTICIPANTS_STALE_TIME_OUTSIDE_MEETING_MS = 30_000;

/** Formulaire participant, ou bascule « masqué » de la liste d'édition. */
type ParticipantUpdate = ParticipantUpdatePayload &
  Pick<MeetingParticipantPatch, "hidden">;

/** Payload du formulaire participant → PATCH v2 : la date de naissance part
 * en ``YYYY-MM-DD`` (les transformers hey-api ne couvrent que les réponses). */
const toParticipantPatch = ({
  id: _id,
  birthdate,
  ...fields
}: ParticipantUpdate): MeetingParticipantPatch => ({
  ...fields,
  ...(birthdate && {
    birthdate: birthdate.toISOString().slice(0, 10) as unknown as Date,
  }),
});

const useParticipantsStaleTime = () => {
  const isActive = useIsActive();
  return () =>
    isActive.value ? Infinity : PARTICIPANTS_STALE_TIME_OUTSIDE_MEETING_MS;
};

/** Codes ninja v2 émis par `resolve_stagiaires_or_400` quand un participant
 * n'est plus assignable (désinscrit ou rôle changé). On invalide alors les
 * queries pour que la liste se rafraîchisse. */
const STALE_PARTICIPANT_CODES = new Set([
  "wrong-participant-meeting",
  "wrong-participant-role",
]);

const isStaleParticipantsError = (error: ApiError): boolean => {
  // v1 DRF : ``{ participants: ["..."] }``
  if (error.hasFieldError("participants")) return true;
  // v2 ninja BusinessRuleError : ``{ detail, code }``
  const apiErrors = error.apiErrors as { code?: unknown } | null | undefined;
  return (
    typeof apiErrors === "object" &&
    apiErrors !== null &&
    typeof apiErrors.code === "string" &&
    STALE_PARTICIPANT_CODES.has(apiErrors.code)
  );
};

export const useStaleParticipantsErrorHandler = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  invalidatePredicate: (query: any) => boolean,
) => {
  const queryClient = useQueryClient();
  const notifyStore = useNotifyStore();

  return (error: unknown) => {
    if (!(error instanceof ApiError) || !isStaleParticipantsError(error)) {
      return;
    }
    queryClient.invalidateQueries({ predicate: invalidatePredicate });
    notifyStore.notifyError(
      "Un participant n'est plus disponible (désinscrit). La liste a été actualisée, merci de réessayer.",
      NotificationType.Warning,
    );
  };
};

export const useParticipants = () => {
  const meetingId = useMeetingId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "participants"] as const,
    queryFn: () =>
      apiCall(listParticipants({
        path: {
          meeting_id: unref(meetingId)!,
        },
      })),
    enabled: () => Boolean(unref(meetingId)) && hasPermission.value("view_meetingparticipant", unref(meetingId)),
    staleTime: useParticipantsStaleTime(),
    select: (data) => data.items,
  });
};

export const useInterns = () => {
  const meetingId = useMeetingId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "participants", "interns"] as const,
    queryFn: () =>
      apiCall(listParticipants({
        path: {
          meeting_id: unref(meetingId)!,
        },
      })),
    enabled: () => Boolean(unref(meetingId)) && hasPermission.value("view_meetingparticipant", unref(meetingId)),
    staleTime: useParticipantsStaleTime(),
    select: (data) => data.items.filter(internFilter),
  });
};

export const useSupervisors = () => {
  const meetingId = useMeetingId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "participants", "supervisors"] as const,
    queryFn: () =>
      apiCall(listParticipants({
        path: {
          meeting_id: unref(meetingId)!,
        },
      })),
    enabled: () => Boolean(unref(meetingId)) && hasPermission.value("view_meetingparticipant", unref(meetingId)),
    staleTime: useParticipantsStaleTime(),
    select: (data) => data.items.filter(supervisorFilter),
  });
};

export const useOpinionParticipants = (
  getCommentUrl: (data: { meetingId: string; participantId: string }) => string,
) => {
  const meetingId = useMeetingId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "opinion-participants"] as const,
    queryFn: () =>
      apiCall(listParticipantOpinions({
        path: {
          meeting_id: unref(meetingId)!,
        },
      })),
    enabled: () => Boolean(unref(meetingId)) && hasPermission.value("view_meetingparticipant", unref(meetingId)),
    select: (data) =>
      data.items.filter(internFilter).map((participant) => {
        try {
          const comment_url = getCommentUrl({
            meetingId: unref(meetingId)!,
            participantId: participant.id,
          });

          return {
            ...participant,
            comment_url,
          };
        } catch (e) {
          console.error("Error in getCommentUrl", e);
        }
      }),
  });
};

export const useOpinionParticipant = () => {
  const meetingId = useMeetingId();
  const stagiaireId = useStagiaireId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "opinion-participants", stagiaireId] as const,
    queryFn: () =>
      apiCall(retrieveParticipantOpinion({
        path: {
          meeting_id: unref(meetingId)!,
          participant_id: unref(stagiaireId)!,
        },
      })),
    enabled: () => Boolean(unref(meetingId)) && hasPermission.value("view_meetingparticipant", unref(meetingId)) && Boolean(unref(stagiaireId)),
  });
};

export const useObservationCountByParticipant = () => {
  const meetingId = useMeetingId();
  const hasPermission = usePermission();

  return useQuery({
    queryKey: ["meetings", meetingId, "participants", "observation-count"] as const,
    queryFn: () =>
      apiCall(listParticipants({
        path: {
          meeting_id: unref(meetingId)!,
        },
      })),
    enabled: () => Boolean(unref(meetingId)) && hasPermission.value("view_meetingcomment", unref(meetingId)),
    select: (data) => {
      const interns = data.items?.filter(
        (participant) => participant.role?.intranet_id === 0,
      );

      return interns?.reduce(
        (acc, p) => ({ ...acc, [p.id]: p.observation_count }),
        {},
      );
    },
  });
};

export const useCurrentStagiaire = () => {
  const meetingId = useMeetingId();
  const stagiaireId = useStagiaireId();

  return useQuery({
    queryKey: ["meetings", meetingId, "participants", stagiaireId] as const,
    queryFn: () =>
      apiCall(retrieveParticipant({
        path: {
          meeting_id: unref(meetingId)!,
          participant_id: unref(stagiaireId)!,
        },
      })),
  });
};

export const useSearchParticipants = (filter: Ref<string>) => {
  const meetingId = useMeetingId();
  return useQuery({
    queryKey: ["meetings", meetingId, "participants", "search", filter] as const,
    queryFn: () =>
      apiCall(listParticipants({
        path: {
          meeting_id: unref(meetingId)!,
        },
      })),
    enabled: () => Boolean(unref(filter)) && Boolean(unref(meetingId)),
    // La liste v2 n'expose pas ``search``/``ordering`` : on filtre/trie côté client.
    select: (data) => {
      const term = unref(filter).trim().toLowerCase();
      const matches = data.items
        .filter((p) => {
          const haystack = [
            p.usage_first_name,
            p.first_name,
            p.usage_last_name,
            p.last_name,
            p.email,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        })
        .sort((a, b) =>
          (a.first_name ?? "").localeCompare(b.first_name ?? ""),
        )
        .slice(0, 10);
      return { items: matches, count: matches.length };
    },
  });
};

export const useUpdateOpinionParticipantMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (participant: OpinionParticipantIn & { id: string }) =>
      apiCall(patchParticipantOpinion({
        path: {
          meeting_id: unref(meetingId)!,
          participant_id: participant.id,
        },
        body: {
          js_opinion: participant.js_opinion,
          sgdf_opinion: participant.sgdf_opinion,
          session_status: participant.session_status,
        },
      })),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["meetings", unref(meetingId), "participants"],
      });
      queryClient.invalidateQueries({
        queryKey: ["comments", unref(meetingId)], // invalide la liste des commentaires à gauche car une observation a été modifiée
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [type, id, entity, entityId] = query.queryKey;
          return (
            (type === "meetings" &&
              id === unref(meetingId) &&
              entity === "opinion-participants" &&
              !entityId) ||
            entityId === data.id
          );
        },
      });
      onSuccess?.();
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

export const useUpdateParticipantMutation = () => {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (participant: ParticipantUpdate) =>
      apiCall(patchParticipant({
        path: {
          meeting_id: unref(meetingId)!,
          participant_id: participant.id,
        },
        body: toParticipantPatch(participant),
      })),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [type, id, entity, entityId] = query.queryKey;
          return (
            (type === "meetings" &&
              id === unref(meetingId) &&
              entity === "opinion-participants" &&
              !entityId) ||
            entityId === data.id
          );
        },
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [type, id, entity, entityId] = query.queryKey;
          return (
            (type === "meetings" &&
              id === unref(meetingId) &&
              entity === "participants" &&
              !entityId) ||
            entityId === data.id
          );
        },
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [type, id, entity, entityId] = query.queryKey;
          return (
            type === "meetings" &&
            id === unref(meetingId) &&
            entity === "participants" &&
            entityId === "interns"
          );
        },
      });
    },
  });
};

export const useUpdateMyParticipantMutation = () => {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (participant: ParticipantUpdatePayload) => {
      const { food_regime, num_bafa_bafd, hebergement, grammatical_form } =
        participant;
      const body: OwnParticipationPatch = {
        food_regime,
        num_bafa_bafd,
        hebergement,
        grammatical_form,
      };
      return apiCall(patchMyParticipation({
        path: { id: unref(meetingId)! },
        body,
      }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [type, id, entity] = query.queryKey;
          return (
            type === "meetings" &&
            id === unref(meetingId) &&
            entity === "participants"
          );
        },
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "me",
      });
    },
  });
};

export const useDeleteSignatureMutation = () => {
  const meetingId = useMeetingId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (participant: { id: string }) =>
      apiCall(meetingsParticipantsResetSignatureDestroy({
        path: {
          meeting_id: unref(meetingId)!,
          id: participant.id,
        },
      })),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["meetings", unref(meetingId), "participants"],
      });
      queryClient.invalidateQueries({
        queryKey: ["meetings", unref(meetingId), "opinion-participants"],
      });
    },
  });
};
